/**
 * AuthLoginScreen — one branded password-login screen for all three personas
 * (spec D6a). `persona="consumer"` powers /login (every original LoginScreen AC
 * preserved); `seller` powers /seller/login (accent chrome, OAuth on, register
 * CTA); `admin` powers /admin/login (utility chrome, OAuth OFF, no public
 * signup, admin-invite param consumption).
 *
 * Post-auth routing goes through the shared `resolveAuthDestination` resolver
 * (Task 2 — never re-implement the safe-return / door-default logic here). A
 * safe return_to always wins for every persona; otherwise every persona
 * (including consumer, since SCRUM-1xx: an admin account is a distinct
 * persona and must not slip through the consumer door) fetches /me (via
 * `queryClient.fetchQuery(["me"])`, which also warms the cache the route
 * guards read) to compute the door default or the wrong-door inline state.
 * When the consumer door rejects an admin identity, the session is revoked
 * (`logout()`) rather than leaving a valid admin token sitting in this tab
 * while the "wrong door" message is shown.
 *
 * ── The counter rebuild (2026-08) ────────────────────────────────────────────
 *
 * The three doors used to differ by a motif tone and a heading. That is the
 * shape of SCRUM-145: a user could not tell which counter had rejected them,
 * and neither could a support agent reading a screenshot. Now the DOOR NAMES
 * ITSELF in the masthead — "ВХОД ДЛЯ ПОКУПАТЕЛЕЙ" / "ВХОД ДЛЯ ПРОДАВЦОВ" /
 * "СЛУЖЕБНЫЙ ВХОД" — over a 2px rule in the door's colour, with the shyrdak
 * motif behind it for the two public doors and deliberately absent for the
 * service entrance. `data-persona` and `data-door` are both on `<main>` so a
 * Playwright failure screenshot is self-identifying too.
 *
 * The form is a ruled slip (see `components/slip.tsx`): drawn rules instead of
 * whitespace, caps labels that stay visible while you type, errors beneath the
 * field they belong to. Validation runs on BLUR — never per keystroke, which
 * tells a user their half-typed address is wrong — and a failed submit focuses
 * the first invalid field via react-hook-form's `shouldFocusError`.
 *
 * ERROR CHANNELS. Credential-shaped failures (wrong password, unverified,
 * locked out) stay inline and are the record of what happened. Failures the
 * user can simply retry (no network, unknown server error) additionally get a
 * retry BUTTON in that same panel — the call site is the only place that knows
 * how to resubmit, so it offers it rather than leaving the user to re-find the
 * control they just pressed. Nothing here fires a generic toast.
 *
 * Testids stay `login-*` across personas (e2e disambiguates by URL).
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, KeyRound, OctagonAlert } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Stagger, StaggerItem } from "@/components/motion";
import { getMe } from "@/features/account/api";
import {
  AUTH_INLINE_LINK_CLASS,
  AUTH_LINK_CLASS,
  AuthDivider,
  AuthLink,
  AuthSubmit,
} from "@/features/auth/components/controls";
import { AuthCounter } from "@/features/auth/components/AuthCounter";
import { OAuthButtons } from "@/features/auth/components/OAuthButtons";
import { PasswordField } from "@/features/auth/components/PasswordField";
import {
  SLIP_INPUT_CLASS,
  Slip,
  SlipField,
  SlipNotice,
} from "@/features/auth/components/slip";
import {
  DOOR_DEFAULT,
  resolveAuthDestination,
  safeReturnToOrNull,
  withReturnTo,
  type AuthPersona,
  type PersonaIdentity,
} from "@/features/auth/destination";
import {
  loginDefaultValues,
  loginSchema,
  type LoginFormValues,
} from "@/features/auth/schemas";
import { logout } from "@/features/settings/api";
import { ApiError, NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";
import { queryClient } from "@/lib/queryClient";

interface LoginResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  user: {
    id: string;
    email: string;
    language: "ru" | "ky" | "en";
  };
}

interface LoginErrorState {
  rootKey: string | null;
  rootParams?: Record<string, string | number>;
  /**
   * Show the "sign up again to get a new verification code" link
   * (routing to `/signup?email=<emailEncoded>`) below the root error.
   * Surfaces on `auth.email_unverified` only.
   */
  showUnverifiedRecovery: boolean;
  /**
   * True when pressing the same button again is a plausible fix — a dropped
   * connection or an unclassified 5xx. Wrong credentials and a lockout are
   * NOT retryable and must not offer a retry that will fail identically.
   */
  retryable: boolean;
}

interface PersonaConfig {
  /** Copy-key prefix within the `auth` namespace (drives title/lede/wrong-door). */
  copy: "login" | "sellerLogin" | "adminLogin";
  showOAuth: boolean;
  footer: "signup" | "register" | "none";
}

const PERSONA: Record<AuthPersona, PersonaConfig> = {
  consumer: { copy: "login", showOAuth: true, footer: "signup" },
  seller: { copy: "sellerLogin", showOAuth: true, footer: "register" },
  admin: { copy: "adminLogin", showOAuth: false, footer: "none" },
};

function mapLoginError(err: unknown, retryAfter: number | undefined): LoginErrorState {
  if (err instanceof NetworkError) {
    return { rootKey: "errors.network", showUnverifiedRecovery: false, retryable: true };
  }
  if (!(err instanceof ApiError)) {
    return { rootKey: "errors.generic", showUnverifiedRecovery: false, retryable: true };
  }
  switch (err.code) {
    case "auth.invalid_credentials":
      return {
        rootKey: "errors.auth.invalid_credentials",
        showUnverifiedRecovery: false,
        retryable: false,
      };
    case "auth.email_unverified":
      return {
        rootKey: "errors.auth.email_unverified",
        showUnverifiedRecovery: true,
        retryable: false,
      };
    case "auth.locked": {
      if (typeof retryAfter === "number" && retryAfter > 0) {
        const minutes = Math.max(1, Math.ceil(retryAfter / 60));
        return {
          rootKey: "errors.auth.locked",
          rootParams: { minutes },
          showUnverifiedRecovery: false,
          retryable: false,
        };
      }
      return {
        rootKey: "errors.auth.locked.generic",
        showUnverifiedRecovery: false,
        retryable: false,
      };
    }
    default:
      return { rootKey: "errors.generic", showUnverifiedRecovery: false, retryable: true };
  }
}

export function AuthLoginScreen({ persona }: { persona: AuthPersona }): ReactElement {
  const cfg = PERSONA[persona];
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const [errorState, setErrorState] = useState<LoginErrorState>({
    rootKey: null,
    showUnverifiedRecovery: false,
    retryable: false,
  });
  // SCRUM-145: a route guard can hand an already-signed-in visitor here when
  // their account doesn't belong to this door (a seller who typed `/admin`).
  // Without the flag they'd get a bare sign-in form and no explanation of why
  // the panel they asked for never opened. Read once, at mount: `onSubmit`
  // owns the state from then on.
  const [wrongDoor, setWrongDoor] = useState(
    () => searchParams.get("wrong_door") === "1",
  );
  const [showSetPassword, setShowSetPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: loginDefaultValues,
    mode: "onBlur",
  });

  const rawReturnTo = searchParams.get("return_to");
  // Where OAuth should land: the door default unless a safe return_to is set.
  const oauthReturnTo = safeReturnToOrNull(rawReturnTo) ?? DOOR_DEFAULT[persona];

  // Admin door only: consume the admin-invite redirect params (closes the
  // previously-dead /admin/login redirect in router_admin_invitations.py).
  useEffect(() => {
    if (persona !== "admin") return;
    const accepted = searchParams.get("invitation_accepted") === "1";
    const already = searchParams.get("already_admin") === "1";
    const setPw = searchParams.get("set_password") === "1";
    if (already) notify.info(t("adminLogin.alreadyAdminToast"), { id: "admin-invite" });
    else if (accepted)
      notify.success(t("adminLogin.inviteAcceptedToast"), { id: "admin-invite" });
    if (setPw) setShowSetPassword(true);
    // run once on mount for the captured params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona]);

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    setErrorState({ rootKey: null, showUnverifiedRecovery: false, retryable: false });
    setWrongDoor(false);
    try {
      const data = await apiClient.post<LoginResponse, LoginFormValues>(
        AUTH_PATHS.login,
        values,
        { skipRefreshOn401: true },
      );
      setAccessToken(data.access_token);
      // SCRUM-138: an explicit logout clears the TanStack cache, but a
      // session that ends WITHOUT one (token expiry -> re-login as a
      // different account, no manual logout) left the previous user's
      // cached queries (chat threads, etc.) in place for the new session
      // to read until each query's own staleTime happened to expire.
      // Clear on every successful login too, belt-and-suspenders.
      queryClient.clear();

      // Safe return_to wins for every persona.
      const safe = safeReturnToOrNull(rawReturnTo);
      if (safe !== null) {
        navigate(safe, { replace: true });
        return;
      }
      // Every door (including consumer) without a return_to resolves by
      // identity. Fetching /me here also warms the ["me"] cache the route
      // guards read.
      let identity: PersonaIdentity;
      try {
        const meData = await queryClient.fetchQuery({
          queryKey: ["me"],
          queryFn: getMe,
        });
        identity = {
          permissions: meData.permissions,
          is_seller: meData.is_seller,
        };
      } catch {
        // /me failed post-login: fall back to the door default; the route
        // guard sorts out persona (mirrors SellerGuard's optimistic posture).
        navigate(DOOR_DEFAULT[persona], { replace: true });
        return;
      }
      const dest = resolveAuthDestination(persona, null, identity);
      if (dest.path !== null) {
        navigate(dest.path, { replace: true });
        return;
      }
      if (persona === "consumer") {
        // An admin account reached the consumer door: this is a genuine
        // rejection, not a soft nudge — revoke the session (refresh cookie +
        // in-memory token) instead of leaving a valid admin token sitting in
        // this tab while the "wrong door" message is shown.
        await logout();
      }
      setWrongDoor(true);
    } catch (err) {
      const retryAfter = err instanceof ApiError ? err.retryAfterSeconds : undefined;
      setErrorState(mapLoginError(err, retryAfter));
    }
  };

  const submit = form.handleSubmit(onSubmit);
  const isSubmitting = form.formState.isSubmitting;
  const emailErrorKey = form.formState.errors.email?.message;
  const passwordErrorKey = form.formState.errors.password?.message;

  // Build the `/signup?email=…` href for the unverified-recovery link. We watch
  // the email field so the prefill reflects what the user just typed.
  const emailForRecovery = form.watch("email");
  const recoveryHref = withReturnTo(
    emailForRecovery !== ""
      ? "/signup?email=" + encodeURIComponent(emailForRecovery)
      : "/signup",
    rawReturnTo,
  );

  return (
    <AuthCounter
      door={persona}
      personaAttr={persona}
      doorName={t(`${cfg.copy}.doorName`)}
      title={t(`${cfg.copy}.title`)}
      lede={t(`${cfg.copy}.lede`)}
      testId="login-screen"
      headerExtra={
        // SCRUM-148: the consumer door is the same sign-in for every account
        // (routing after login is role-based). Sellers who land here anyway
        // get an explicit pointer to their own door instead of a silent guess.
        persona === "consumer" ? (
          <p
            className="mt-1.5 text-[0.8125rem] text-[var(--g-ink-2)]"
            data-testid="login-seller-hint"
          >
            {t("login.sellerHint")}{" "}
            <Link
              to="/seller/login"
              className={AUTH_INLINE_LINK_CLASS}
              data-testid="login-seller-hint-link"
            >
              {t("login.sellerHintLink")}
            </Link>
          </p>
        ) : null
      }
    >
      <Stagger className="gav2-stack-lg flex flex-col">
        {showSetPassword ? (
          <StaggerItem>
            <SlipNotice
              tone="attention"
              icon={<KeyRound {...iconProps("sm")} />}
              testId="login-set-password"
            >
              <p>{t("adminLogin.setPasswordPrompt")}</p>
              <AuthLink to="/forgot-password" testId="login-set-password-link">
                {t("adminLogin.setPasswordCta")}
              </AuthLink>
            </SlipNotice>
          </StaggerItem>
        ) : null}

        {wrongDoor ? (
          <StaggerItem>
            <SlipNotice
              tone="attention"
              icon={<Info {...iconProps("sm")} />}
              title={t(`${cfg.copy}.wrongDoorTitle`)}
              testId="login-wrong-door"
            >
              <p className="text-[var(--g-ink-2)]">
                {t(`${cfg.copy}.wrongDoorBody`)}
              </p>
              <div className="flex flex-wrap items-center gap-x-5">
                {persona === "seller" ? (
                  <AuthLink to="/sellers/register" testId="login-wrong-door-cta">
                    {t("sellerLogin.wrongDoorCta")}
                  </AuthLink>
                ) : null}
                {/* SCRUM-1xx: an admin account reached the consumer door —
                    point straight at the admin door. */}
                {persona === "consumer" ? (
                  <AuthLink to="/admin/login" testId="login-wrong-door-admin-cta">
                    {t("login.wrongDoorAdminCta")}
                  </AuthLink>
                ) : null}
                {/* SCRUM-148: authenticated but not for this door — most
                    likely a regular account. Never shown on the consumer door
                    itself: a link to /login from /login is meaningless. */}
                {persona !== "consumer" ? (
                  <AuthLink to="/login" testId="login-wrong-door-back-link">
                    {t("login.wrongDoorBackLink")}
                  </AuthLink>
                ) : null}
              </div>
            </SlipNotice>
          </StaggerItem>
        ) : null}

        <StaggerItem>
          <form
            className="gav2-stack-md flex flex-col"
            onSubmit={submit}
            noValidate
            aria-describedby={errorState.rootKey !== null ? "login-server-error" : undefined}
          >
            <Slip>
              <SlipField
                htmlFor="login-email"
                label={t("login.emailLabel")}
                required
                requiredLabel={t("common.requiredField")}
                error={emailErrorKey !== undefined ? t(emailErrorKey) : undefined}
                errorId="login-email-error"
                errorTestId="login-email-error"
              >
                <input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  aria-invalid={emailErrorKey !== undefined || undefined}
                  aria-describedby={emailErrorKey !== undefined ? "login-email-error" : undefined}
                  placeholder={t("login.emailPlaceholder")}
                  disabled={isSubmitting}
                  data-testid="login-email"
                  className={SLIP_INPUT_CLASS}
                  {...form.register("email")}
                />
              </SlipField>

              <PasswordField
                id="login-password"
                label={t("login.passwordLabel")}
                autoComplete="current-password"
                placeholder={t("login.passwordPlaceholder")}
                disabled={isSubmitting}
                data-testid="login-password"
                aria-describedby={
                  passwordErrorKey !== undefined ? "login-password-error" : undefined
                }
                error={passwordErrorKey !== undefined ? t(passwordErrorKey) : undefined}
                errorId="login-password-error"
                errorTestId="login-password-error"
                {...form.register("password")}
              />
            </Slip>

            <div className="-mt-1 flex justify-end">
              <AuthLink
                to={withReturnTo("/forgot-password", rawReturnTo)}
                testId="login-forgot-link"
                className="text-[0.8125rem]"
              >
                {t("login.forgotPasswordLink")}
              </AuthLink>
            </div>

            {errorState.rootKey !== null ? (
              <SlipNotice
                tone="error"
                role="alert"
                id="login-server-error"
                icon={<OctagonAlert {...iconProps("sm")} />}
              >
                <p data-testid="login-server-error">
                  {t(errorState.rootKey, errorState.rootParams)}
                </p>
                {errorState.showUnverifiedRecovery ? (
                  <p className="mt-1 text-[var(--g-ink-2)]">
                    {t("login.unverifiedRetryHint")}{" "}
                    <Link
                      to={recoveryHref}
                      className={AUTH_INLINE_LINK_CLASS}
                      data-testid="login-unverified-recovery-link"
                    >
                      {t("login.unverifiedRetryLink")}
                    </Link>
                  </p>
                ) : null}
                {errorState.retryable ? (
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={isSubmitting}
                    data-testid="login-error-retry"
                    className={"mt-1 " + AUTH_LINK_CLASS}
                  >
                    {t("common.retry")}
                  </button>
                ) : null}
              </SlipNotice>
            ) : null}

            <AuthSubmit
              pending={isSubmitting}
              pendingLabel={t("login.submitting")}
              testId="login-submit"
            >
              {t("login.submit")}
            </AuthSubmit>
          </form>
        </StaggerItem>

        {cfg.showOAuth ? (
          <StaggerItem className="gav2-stack-md flex flex-col">
            <AuthDivider label={t("login.orDivider")} />
            <OAuthButtons returnTo={oauthReturnTo} disabled={isSubmitting} />
          </StaggerItem>
        ) : null}

        {cfg.footer === "signup" ? (
          <StaggerItem className="text-center text-sm text-[var(--g-ink-2)]">
            {t("login.noAccount")}{" "}
            <Link
              to={withReturnTo("/signup", rawReturnTo)}
              className={AUTH_INLINE_LINK_CLASS}
              data-testid="login-noaccount-link"
            >
              {t("login.noAccountLink")}
            </Link>
          </StaggerItem>
        ) : null}
        {cfg.footer === "register" ? (
          <StaggerItem className="text-center text-sm text-[var(--g-ink-2)]">
            {t("sellerLogin.registerPrompt")}{" "}
            <Link
              to="/sellers/register"
              className={AUTH_INLINE_LINK_CLASS}
              data-testid="login-register-link"
            >
              {t("sellerLogin.registerLink")}
            </Link>
          </StaggerItem>
        ) : null}
      </Stagger>
    </AuthCounter>
  );
}

export default AuthLoginScreen;
